import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import GameCard from '@/components/base/GameCard';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getGamesByCategory } from '@/mocks/games';
import type { Game } from '@/mocks/games';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const [games, setGames] = useState<Game[]>([]);
  const [activeCategory, setActiveCategory] = useState(slug || '');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setGames(getGamesByCategory(slug));
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-14 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，未找到该游戏分类。</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium text-sm whitespace-nowrap">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - h游戏${category.name}推荐`,
    description: `${category.description}。浏览h游戏${category.name}合集，查看最新${category.name}类h游戏推荐与排行。`,
    url: `https://xinchengtongxun.cn/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: games.map((game, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'VideoGame',
          name: game.title,
          url: `https://xinchengtongxun.cn/category/${game.categorySlug}`,
        },
      })),
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Category Hero */}
        <section className="relative pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mb-3">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${category.icon} text-xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {category.name}游戏
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {category.description} · 共 <strong>{category.gameCount}</strong> 款游戏
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Game Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {games.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-product-shop="">
              {games.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-gamepad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无游戏，敬请期待！</p>
            </div>
          )}
        </section>

        <Footer />
      </div>
    </>
  );
}